import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { FLASHCARDS } from '@/constants/testIds';
import { Check, Eye, EyeOff, RotateCw, Loader2, ChevronRight, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'sonner';

export default function Flashcards() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [importance, setImportance] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get('/books').then(({ data }) => setBooks(data.books || [])).catch(() => {});
  }, []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBooks.length) params.set('books', selectedBooks.join(','));
      if (importance !== 'all') params.set('importance', importance);
      const { data } = await api.get(`/flashcards/queue?${params.toString()}`);
      setQueue(data.items || []);
      setIndex(0);
      setFlipped(false);
    } catch {
      toast.error('Could not load flashcards');
    } finally {
      setLoading(false);
    }
  }, [selectedBooks, importance]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    loadQueue();
  }, [user, authLoading, loadQueue]);

  if (!authLoading && !user) {
    return (
      <div className="container-page py-24 text-center max-w-md mx-auto">
        <p className="font-serif-display text-4xl text-slate-900 mb-3">Flashcards</p>
        <p className="text-slate-600 mb-8">Sign in to track which people you know, want to learn, or want to skip.</p>
        <button onClick={() => navigate('/login')} className="btn-primary">Sign in to start</button>
      </div>
    );
  }

  const current = queue[index];

  const handleMark = async (status) => {
    if (!current) return;
    try {
      await api.post(`/flashcards/${current.id}`, { status });
      const labels = { known: 'Marked as Known', want_to_know: 'Saved to Want to Know', ignore: 'Ignored' };
      toast.success(labels[status]);
      if (index + 1 >= queue.length) {
        loadQueue();
      } else {
        setIndex((i) => i + 1);
        setFlipped(false);
      }
    } catch {
      toast.error('Could not update');
    }
  };

  const handleNext = () => {
    if (index + 1 >= queue.length) loadQueue();
    else { setIndex((i) => i + 1); setFlipped(false); }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!current) {
    return (
      <div className="container-page py-24 text-center max-w-xl mx-auto" data-testid={FLASHCARDS.page}>
        <p className="font-serif-display text-4xl text-slate-900 mb-3">You're all caught up</p>
        <p className="text-slate-600 mb-8">
          You've reviewed every person matching your filters. Reset filters or revisit your bookmarks for further study.
        </p>
        <div className="flex justify-center gap-3">
          <button onClick={loadQueue} className="btn-secondary">
            <RotateCw className="w-4 h-4" /> Reload deck
          </button>
          <Link to="/bookmarks" className="btn-primary">View bookmarks</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-12 lg:py-16" data-testid={FLASHCARDS.page}>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif-display text-4xl text-slate-900">Study Deck</h1>
            <p className="text-sm text-slate-500 mt-1">
              Card {index + 1} of {queue.length} · Tap the card to flip
              {(selectedBooks.length > 0 || importance !== 'all') && (
                <span className="ml-2 text-[#0F766E]">
                  · Filtered ({importance !== 'all' ? importance : 'All'}, {selectedBooks.length || 'all'} book{selectedBooks.length === 1 ? '' : 's'})
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowFilters(s => !s)} className="btn-secondary text-sm py-2 px-4">
              <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
            </button>
            <button onClick={loadQueue} className="btn-secondary text-sm py-2 px-4">
              <RotateCw className="w-3.5 h-3.5" /> Reshuffle
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2 block">Importance</label>
              <div className="grid grid-cols-3 gap-2 max-w-md">
                {['all', 'Major', 'Minor'].map(t => (
                  <button key={t} onClick={() => setImportance(t)}
                    className={`px-3 py-2 text-sm rounded-md border transition-all ${importance === t ? 'bg-[#0F766E] text-white border-[#0F766E]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
                    {t === 'all' ? 'Both' : t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                  Books {selectedBooks.length > 0 && <span className="text-[#0F766E]">({selectedBooks.length} selected)</span>}
                </label>
                {selectedBooks.length > 0 && (
                  <button onClick={() => setSelectedBooks([])} className="text-xs text-slate-500 hover:text-[#1E3A8A] flex items-center gap-1">
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 max-h-64 overflow-y-auto p-1">
                {books.map(b => {
                  const active = selectedBooks.includes(b.book);
                  return (
                    <button key={b.book} onClick={() => setSelectedBooks(s => active ? s.filter(x => x !== b.book) : [...s, b.book])}
                      className={`text-xs px-2.5 py-1.5 rounded-md border text-left truncate transition-all ${active ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>
                      {b.book} <span className="opacity-60">({b.count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end mt-4 pt-4 border-t border-slate-100">
              <button onClick={() => { loadQueue(); setShowFilters(false); }} className="btn-primary text-sm py-2 px-4">Apply & shuffle</button>
            </div>
          </div>
        )}

        <div className="[perspective:1500px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id + (flipped ? '-back' : '-front')}
              initial={{ rotateY: flipped ? -90 : 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: flipped ? 90 : -90, opacity: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setFlipped((f) => !f)}
              data-testid={FLASHCARDS.card}
              className="bg-white border-2 border-slate-200 rounded-2xl p-10 lg:p-14 min-h-[400px] flex flex-col items-center justify-center text-center cursor-pointer hover:border-slate-300 transition-colors"
            >
              {!flipped ? (
                <>
                  <span className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-5">{current.book}</span>
                  <div className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-2xl bg-gradient-to-br from-[#CCFBF1] via-[#DBEAFE] to-[#F8FAFC] border border-slate-200 p-4 flex items-center justify-center overflow-hidden mb-6">
                    <img
                      src={current.image_url || '/images/icon-placeholder.png'}
                      alt={current.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h2 className="font-serif-display text-5xl lg:text-6xl tracking-tight text-slate-900 mb-3">
                    {current.name}
                  </h2>
                  <p className="font-mono text-sm text-[#0F766E]">{current.scripture_reference}</p>
                  <p className="text-xs text-slate-400 mt-6 uppercase tracking-widest">Tap to reveal</p>
                </>
              ) : (
                <>
                  <span className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-3">
                    {current.importance} · {current.testament === 'Old' ? 'OT' : 'NT'}
                  </span>
                  <h3 className="font-serif-display text-3xl text-slate-900 mb-4">{current.name}</h3>
                  <p className="text-base leading-relaxed text-slate-700 max-w-xl">{current.details}</p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-8">
          <button
            data-testid={FLASHCARDS.markIgnore}
            onClick={() => handleMark('ignore')}
            className="flex flex-col items-center gap-1.5 py-4 px-3 rounded-lg border border-slate-200 hover:border-rose-400 hover:bg-rose-50 transition-all group"
          >
            <EyeOff className="w-5 h-5 text-rose-500" />
            <span className="text-sm font-medium text-slate-700">Ignore</span>
          </button>
          <button
            data-testid={FLASHCARDS.markWant}
            onClick={() => handleMark('want_to_know')}
            className="flex flex-col items-center gap-1.5 py-4 px-3 rounded-lg border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-all"
          >
            <Eye className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">Want to Know</span>
          </button>
          <button
            data-testid={FLASHCARDS.markKnown}
            onClick={() => handleMark('known')}
            className="flex flex-col items-center gap-1.5 py-4 px-3 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
          >
            <Check className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-medium text-slate-700">Known</span>
          </button>
        </div>

        <button
          data-testid={FLASHCARDS.nextButton}
          onClick={handleNext}
          className="mt-3 w-full text-sm text-slate-500 hover:text-[#1E3A8A] transition-colors py-2 flex items-center justify-center gap-1"
        >
          Skip <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
